import React from "react";
import { Navigate } from "react-router-dom";
import { FeatureFlagGuard } from "Components/FeatureFlagGuard/FeatureFlagGuard";
import { useAdminConsoleAccess } from "Components/Authentication/useRoleAccess";
import { OnlyForAdminGuard, RoleGuard } from "./NavigationGuards";
import { AdminConsolePage } from "Pages/AdminConsole/AdminConsole.page";
import { AuditHistoryPage } from "Pages/AdminConsole/AuditHistory.page";
import { RealTimeMapPage } from "Pages/RealTimeMap/RealTimeMap.page";
import { MissionPlannerPage } from "Pages/MissionPlanner/MissionPlanner.page";
import { WelcomePage } from "Pages/Welcome/Welcome.page";
import { VehiclesListPage } from "Pages/VehiclesList/VehiclesList.page";
import { VehicleTypesListPage } from "Pages/VehicleTypesList/VehicleTypesList.page";
import { FleetsListPage } from "Pages/FleetsList/FleetsList.page";
import { UsersListPage } from "Pages/UsersList/UsersList.page";
import { OTAManagementPage } from "Pages/OTAManagement/OTAManagement.page";
import { IntersectionsPage } from "Pages/Intersections/Intersections.page";
import { StationViewPage } from "Pages/StationView/StationView.page";
import { LiveMissionsPage } from "Pages/LiveMissions/LiveMissions.page";
import { LiveMissionsPage as LiveMissionsPageWrapper } from "Pages/LiveMissionsPage/LiveMissionsPage.page";
import { QueueManagerPage } from "Pages/QueueManagerPage/QueueManagerPage.page";
import { VCMSPage } from "Pages/VCMS/VCMS.page";

export const QUEUE_MISSIONS_PATH = "/queued_missions";
export const LIVE_MISSIONS_PATH = "/live_missions";
export const MISSIONS_PATH = "/missions";

/**
 * `/people` in the dual-mode cutover (DEV-3767): a session that uses the Admin
 * Console (`Cyngn-Admin` + `ADMIN_CONSOLE_ENABLED`) is moved to `/admin`;
 * everyone else — legacy admins included — keeps today's page, so nothing is
 * lost while the flag is dormant.
 */
const PeopleRoute = () => {
  const usesAdminConsole = useAdminConsoleAccess();

  if (usesAdminConsole) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <OnlyForAdminGuard>
      <UsersListPage />
    </OnlyForAdminGuard>
  );
};

/**
 * `/admin/*` behind its RoleGuard: the Admin Console when
 * `ADMIN_CONSOLE_ENABLED` is on, otherwise a forward to `/people` so the route
 * stays inert (plan §6.1) while still enforcing the plan's rule (§3.6) that
 * `/admin` requires `Cyngn-Admin`. Every console screen — user management and
 * the audit history (DEV-5969) — goes through this one gate.
 */
const AdminRoute = ({ children }: { children: React.ReactElement }) => {
  const usesAdminConsole = useAdminConsoleAccess();

  if (!usesAdminConsole) {
    return <Navigate to="/people" replace />;
  }

  return children;
};

export const AuthorizedRouter = [
  {
    path: "",
    element: <RealTimeMapPage />,
  },
  {
    path: "/station",
    element: <StationViewPage />,
  },
  {
    // Preserve old bookmarks: redirect /operator to /station for one release cycle.
    path: "/operator",
    element: <Navigate to="/station" replace />,
  },
  {
    path: "/intersections",
    element: <IntersectionsPage />,
  },
  {
    path: MISSIONS_PATH,
    element: <MissionPlannerPage />,
  },
  {
    path: `${MISSIONS_PATH}/list`,
    element: <MissionPlannerPage />,
  },
  {
    path: "",
    element: <LiveMissionsPageWrapper />,
    children: [
      {
        path: LIVE_MISSIONS_PATH,
        element: (
          <FeatureFlagGuard flag="LIVE_MISSIONS">
            <LiveMissionsPage />
          </FeatureFlagGuard>
        ),
      },
      {
        path: QUEUE_MISSIONS_PATH,
        element: (
          <FeatureFlagGuard flag="QUEUE_MANAGER">
            <QueueManagerPage />
          </FeatureFlagGuard>
        ),
      },
    ],
  },
  {
    path: "/welcome",
    element: <WelcomePage />,
  },
  {
    path: "/vehicles",
    element: (
      <OnlyForAdminGuard>
        <VehiclesListPage />
      </OnlyForAdminGuard>
    ),
  },
  {
    path: "/vehicletypes",
    element: (
      <OnlyForAdminGuard>
        <VehicleTypesListPage />
      </OnlyForAdminGuard>
    ),
  },
  {
    path: "/fleets",
    element: (
      <OnlyForAdminGuard>
        <FleetsListPage />
      </OnlyForAdminGuard>
    ),
  },
  {
    path: "/people",
    element: <PeopleRoute />,
  },
  {
    path: "/admin/history",
    element: (
      <RoleGuard roles={["Cyngn-Admin"]}>
        <AdminRoute>
          <AuditHistoryPage />
        </AdminRoute>
      </RoleGuard>
    ),
  },
  {
    path: "/admin/*",
    element: (
      <RoleGuard roles={["Cyngn-Admin"]}>
        <AdminRoute>
          <AdminConsolePage />
        </AdminRoute>
      </RoleGuard>
    ),
  },
  {
    path: "/vcms/*",
    element: <VCMSPage />,
  },
  {
    path: "/ota",
    element: (
      <OnlyForAdminGuard>
        <OTAManagementPage />
      </OnlyForAdminGuard>
    ),
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
];
